import axios from "axios";
import store from "@/plugins/store";

export default {
    methods: {
      /**
       * Save a registration either to the server or to the outbox as a fallback when server time-out or gives an error.
       *
       * @param registration
       * @param concept
       * @param guestToken When provided, the registration will only be sent and not stored in the outbox.
       * @returns {Promise<{success: boolean, message: string}>}
       */
      async saveRegistration(registration, concept, guestToken = null) {
        // Validate registration object

        if (!registration || typeof registration !== 'object') {
          return {
            success: false,
            message: 'Invalid parameter registration.',
          };
        }
        // Validate concept flag
        if (typeof concept !== 'boolean') {
          return {
            success: false,
            message: 'Invalid parameter concept flag.',
          };
        }

        // Set registration process data.
        registration.registration_process = {}; // Create object if not exists, overwrite if exists to make sure we start fresh and don't carry over old errors and job statuses.
        registration.registration_process.concept = concept;

        // Set registration update date
        let date_now = new Date();
        registration.registration_updated_at = date_now.toISOString()

        // First try to send the registration to the server
        let serverResponse = await this.sendRegistrationToServer(registration, guestToken);
        // Check if the sending was successful
        if (serverResponse.success === true) {
          return {
            success: true,
            message: 'De registratie is succesvol opgeslagen op de server.',
          }
        }

        // If we have a guest token, we do not store the registration in the outbox
        if (guestToken) {
          return {
            success: false,
            message: 'De registratie kon niet worden verstuurd naar de server.',
          }
        }

        // Save the registration to the outbox
        let storeResponse = await this.saveRegistrationToOutbox(registration);
        // Check if the saving was successful
        if (storeResponse.success === true) {
          return {
            success: true,
            message: 'De registratie is succesvol opgeslagen in de lokaal opgeslagen registraties.',
          }
        }

        return {
          success: false,
          message: 'De registratie kon niet worden verstuurd of opgeslagen worden in de lokaal opgeslagen registraties.',
        };
      },
      /**
       * Send a single registration to the server.
       *
       * @param registration
       * @param guestToken
       * @returns {Promise<{success: boolean, message: string, status: number}>}
       */
      async sendRegistrationToServer(registration, guestToken = null) {
        // Send registration to server
        try {
          // Get form ID
          let form_id = registration.form_id;

          // Set timeout and optionally the guest token
          let config = {
            // headers: { 'Content-Type': 'application/json; charset=utf-8' }, // Force content type, prevent axios from setting to multipart/form-data and maybe prevent the error "BadRequestHttpException: Control character error, possibly incorrectly encoded" on the backend
            // transformRequest: [(data) => JSON.stringify(data)], // force stringify
            // Set time out
            timeout: 60000,
            // Upload progress handler
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              let percentage = Math.floor((loaded * 100) / total);
              if (percentage >= 100) {
                this.sendRegistrationPhase = 'server-processing';
              }
              this.sendRegistrationProgress = percentage;
            }
          }
          if (guestToken) {
            config.params = {
              guest_token: guestToken,
            }
          }

          this.sendRegistrationPhase = 'sending';

          let response = await axios.post(`api/form/${form_id}/create-registration`, registration, config)

          // Check if the response status is success
          if (response.status === 201) {
            return {
              success: true,
              message: 'Registratie is opgeslagen op de server.',
              status: response.status,
            };
          }

          return {
            success: false,
            message: 'Er is iets mis gegaan met het opslaan van de registratie op de server.',
            status: response.status,
          }

        } catch (error) {
          // Get the response
          let responseData = error.response ? error.response.data : null;
          let errorMessage = responseData ? responseData.message : 'Er is iets mis gegaan met het opslaan van de registratie op de server.';

          return {
            success: false,
            message: `Onverwachte fout: ${errorMessage}`,
            status: error.response ? error.response.status : 500,
          };
        }
      },
      /**
       * Save a registration to the outbox store.
       *
       * @param registration
       * @returns {Promise<{success: boolean, message: string}>}
       */
      async saveRegistrationToOutbox(registration) {
        try {
          // Save the registration to the outbox store
          let response = await store.dispatch('registrationOutbox/add', registration);
          // Check if response is successful
          if (response.success === true) {
            return {
              success: true,
              message: 'Registratie is opgeslagen in de lokaal opgeslagen registraties.',
            };
          }
          // Error
          return {
            success: false,
            message: 'Er is iets mis gegaan met het opslaan van de registratie in de lokaal opgeslagen registraties',
          };
        }
        catch (error) {
          return {
            success: false,
            message: `Onverwachte fout tijdens het opslaan van de registratie in de lokaal opgeslagen registraties: ${error.message || error}`,
          };
        }
      },
    },
};